import Cookies from 'js-cookie';
import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import { ShortCircuitError } from '../../../../lib/error';
import { recalculateDomains } from '../../../index';
import * as ListenerConstants from '../../constants';
import * as D from '../../deps';
import * as AuthenticationServices from '../../services/Authentication';

const log = D.Logger.child({
  component: 'domains/listener/sagas/workers/Authentication',
});

export function* initialize() {
  yield select(D.Utils.Listener.setSentryClientUserAndMetaData);
}

export function* loginFail() {
  yield D.tryCall(authenticateListener, { isAnon: true, failedLogin: true });
}

export function* register({ payload }) {
  const { username, password, zipcode, gender, birthDay, birthMonth, birthYear, keepLoggedIn } =
    payload;

  // IS UNDERAGE
  const isListenerUnderage = yield select(
    D.Utils.DateUtils.isUnderage,
    birthYear,
    birthMonth,
    birthDay,
  );
  if (isListenerUnderage) {
    const expireTimestamp = new Date(new Date().getTime() + 8 * 60 * 60 * 1000);
    Cookies.set(ListenerConstants.UNDERAGE_BLOCK, true, {
      expires: expireTimestamp,
    });
    throw new ShortCircuitError(D.Constants.ListenerExternals.Errors.USER_IS_UNDERAGE);
  }

  yield call(AuthenticationServices.register, {
    username,
    password,
    zipcode,
    gender,
    birthDay,
    birthMonth,
    birthYear,
    keepLoggedIn,
  });
  // Allow all domains to respond and handle any re-initialization logic
  // TODO: this logic should eventually be moved to mediator once reauth mediator is in place
  yield call(recalculateDomains);
  yield call(
    D.putAndWait,
    D.Actions.Listener.listenerAuthenticated(D.Constants.Listener.AuthTypes.REGISTRATION),
  );
}

// WORKERS

/**
 * [authenticateListener description]
 *
 * @param  {[type]}  [authToken=null]         [description]
 * @param  {boolean} [isAnon=false]           [description]
 * @param  {string}  [username='']            [description]
 * @param  {string}  [password='']            [description]
 * @param  {boolean} [rememberMe=false]       [description]
 * @param  {boolean} [isLoginAttempt=true]    [description]
 * @param  {boolean} [isUserInitiated=false}] [description]
 * @param  {boolean} [failedLogin=false}] [description]
 * @param  {boolean} [isCustomerServiceLogin=false}] [description]
 * @param  {boolean} [isReauth=false] whether or not this auth is treated as a reAuth
 */
export function* authenticateListener({
  accessToken = null,
  authToken = null,
  captchaState,
  deviceUuid = '',
  isAnon = false,
  isCustomerServiceLogin = false,
  isLoginAttempt = true,
  isReauth = false,
  isUserInitiated = false,
  password = '',
  rememberMe = false,
  username = '',
  continueSource = false,
}) {
  log.info({ obj: arguments[0] }, 'authenticateListener(): attempting login');

  if (isUserInitiated || isCustomerServiceLogin) {
    log.info('authenticateListener(): purging persistence');
    if (D.getPersistor()) {
      yield call(D.getPersistor().purge, D.getReducersToPurge());
    }
  }

  try {
    if (accessToken) {
      log.info('authenticateListener(): attempting accessToken login');
      yield call(AuthenticationServices.loginWithAccessToken, accessToken, rememberMe);
      yield call(D.Services.Listener.getExperimentsForListener);
      // Allow all domains to respond and handle any re-initialization logic
      // TODO: this logic should eventually be moved to mediator once reauth mediator is in place
      yield call(recalculateDomains);
      yield call(
        D.putAndWait,
        D.Actions.Listener.listenerAuthenticated(D.Constants.Listener.AuthTypes.ACCESS_TOKEN),
      );
    } else if (deviceUuid) {
      log.info('authenticateListener(): attempting device uuid login');
      yield call(AuthenticationServices.deviceLogin, deviceUuid);
      // Allow all domains to respond and handle any re-initialization logic
      // TODO: this logic should eventually be moved to mediator once reauth mediator is in place
      yield call(recalculateDomains);
      yield call(
        D.putAndWait,
        D.Actions.Listener.listenerAuthenticated(D.Constants.Listener.AuthTypes.DEVICE_LOGIN),
      );
    } else if (isAnon) {
      log.info('authenticateListener(): attempting anon login');
      yield call(AuthenticationServices.anonLogin, {
        authToken,
        isLoginAttempt,
        isCustomerServiceLogin,
      });
      yield call(D.Services.Listener.getExperimentsForListener);
      // Allow all domains to respond and handle any re-initialization logic
      // TODO: this logic should eventually be moved to mediator once reauth mediator is in place
      yield call(recalculateDomains);
      const authType = isCustomerServiceLogin
        ? D.Constants.Listener.AuthTypes.CUSTOMER_SERVICE
        : D.Constants.Listener.AuthTypes.ANONYMOUS;
      yield call(D.putAndWait, D.Actions.Listener.listenerAuthenticated(authType));
    } else {
      log.info('authenticateListener(): attempting registered login');
      yield call(
        AuthenticationServices.login,
        username,
        password,
        rememberMe,
        isUserInitiated,
        authToken,
        isReauth,
        captchaState,
      );
      yield call(D.Services.Listener.getExperimentsForListener);
      // Allow all domains to respond and handle any re-initialization logic
      // TODO: this logic should eventually be moved to mediator once reauth mediator is in place
      yield call(recalculateDomains);

      let authType;
      if (isReauth) {
        authType = D.Constants.Listener.AuthTypes.RE_AUTH;
      } else if (!password && authToken) {
        authType = D.Constants.Listener.AuthTypes.CACHED_LOGIN;
      } else {
        authType = D.Constants.Listener.AuthTypes.MANUAL_LOGIN;
      }

      yield call(D.putAndWait, D.Actions.Listener.listenerAuthenticated(authType));
      // FIXME: this should be moved to the mediator once we have the reauth mediator in place
      yield put(D.Actions.UseCases.handlePremiumAccess(continueSource));
    }
    yield put(D.Actions.Login.rememberLogin(rememberMe));
  } catch (err) {
    if (err instanceof D.Errors.ApiError) {
      log.info(`authenticateListener(): api login failed with error: ${err.errorMessage}`);

      if (err.errorType === D.ApiErrorTypes.READONLY_MODE && !isAnon) {
        log.info('authenticateListener(): in read only mode, redirecting to login path');
        yield put(push(D.Utils.Route.getLoginPath()));
      }
    }
    throw err;
  }
}

/**
 * [reauthListener description]
 */
export function* reauthListener({ payload: { continueSource } }) {
  log.info('reauthListener(): attempting reauth of listener');

  yield put(D.Actions.Login.setAuthTokenNull());

  const options = {
    isLoginAttempt: true,
    isAnon: yield select(D.Utils.Listener.isAnonymousListener),
    authToken: yield select(D.Utils.Listener.getAuthToken),
    username: yield select(D.Utils.Listener.getCurrentUsername),
    rememberMe: yield select(D.Utils.Listener.isRememberMe),
    isUserInitiated: false,
    isReauth: true,
    continueSource,
  };
  // update station recommendations on Listener Reauth.
  // discard any errors
  yield call(authenticateListener, options);
}

/**
 * Log the user out and redirect (hard) to the sign in page
 *
 * @param {object} action
 * @param action.payload
 */
export function* logout({ payload }) {
  log.info('logout(): attempting to logout current listener');

  yield call(AuthenticationServices.logout, payload.username);
}

export function* loginToCommunity() {
  log.info('loginToCommunity(): attempting to login current listener to community site');

  try {
    const authToken = yield select(D.Utils.Listener.getAuthToken);
    if (authToken) {
      yield call(AuthenticationServices.loginToCommunity, authToken);
    }
  } catch (err) {
    log.error(`loginToCommunity(): service call failed with error: ${err.errorMessage}`);
  }
}

export function* logoutFromCommunity() {
  log.info('logoutFromCommunity(): attempting to logout current listener from community site');

  try {
    yield call(AuthenticationServices.logoutFromCommunity);
  } catch (err) {
    log.error(`logoutFromCommunity(): service call failed with error: ${err.errorMessage}`);
  }
}

/*
 * Sends the api request for resetting a user's password, then redirects on success
 */
export function* resetPassword({ payload: { resetToken, newPassword, captchaState } }) {
  log.info('resetPassword(): attempting to reset password');

  try {
    yield call(AuthenticationServices.resetPassword, {
      resetToken,
      newPassword,
      captchaState,
    });
  } catch (err) {
    log.error(`resetPassword(): service call failed with error: ${err.errorMessage}`);
  }
}
